import type { DynamicToolUIPart } from 'ai';
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithTokenHandling } from '../baseQuery';
import { SCOUT_THREAD_LIST_ID, SCOUT_THREAD_TAG_TYPE } from './constants';

export interface ThreadTurn {
  id: number;
  sequence_number: number;
  status: 'completed' | 'interrupted' | 'error' | 'streaming';
  /** Single user message object (not an array). */
  user_message: unknown;
  assistant_text: string | null;
  assistant_parts: AssistantPart[] | null;
  created_at: string;
  updated_at: string;
}

export type AssistantPart =
  | { type: 'text'; text: string }
  | {
      type: 'tool-call';
      toolCallId: string;
      toolName: string;
      input: Record<string, unknown>;
      output: unknown;
      /** `{ structured, renderer }` when the tool returned an MCP structured payload. */
      toolMetadata?: DynamicToolUIPart['toolMetadata'];
    };

export interface ThreadSummary {
  id: number;
  title: string | null;
  session_id: string;
  created_at: string;
  updated_at: string;
}

export interface ScoutThreadDetail extends ThreadSummary {
  turns: ThreadTurn[];
}

interface ListThreadsResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ThreadSummary[];
}

export const scoutThreadsApi = createApi({
  reducerPath: 'scoutThreads',
  baseQuery: baseQueryWithTokenHandling,
  tagTypes: [SCOUT_THREAD_TAG_TYPE],
  endpoints: (builder) => ({
    listScoutThreads: builder.query<ListThreadsResponse, void>({
      query: () => ({ url: '/v1/ai/chat/threads/' }),
      providesTags: [
        { type: SCOUT_THREAD_TAG_TYPE, id: SCOUT_THREAD_LIST_ID },
      ],
    }),
    getScoutThread: builder.query<ScoutThreadDetail, number>({
      query: (id) => ({ url: `/v1/ai/chat/threads/${id}/` }),
      providesTags: (_result, _error, id) => [
        { type: SCOUT_THREAD_TAG_TYPE, id },
      ],
    }),
    updateScoutThread: builder.mutation<ThreadSummary, { id: number; title: string }>({
      query: ({ id, title }) => ({
        url: `/v1/ai/chat/threads/${id}/`,
        method: 'PATCH',
        body: { title },
      }),
      // Show the new title in the list immediately; roll back on failure.
      async onQueryStarted({ id, title }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          scoutThreadsApi.util.updateQueryData(
            'listScoutThreads',
            undefined,
            (draft) => {
              const thread = draft?.results?.find((t) => t.id === id);
              if (thread) thread.title = title;
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (_result, _error, { id }) => [
        { type: SCOUT_THREAD_TAG_TYPE, id: SCOUT_THREAD_LIST_ID },
        { type: SCOUT_THREAD_TAG_TYPE, id },
      ],
    }),
    deleteScoutThread: builder.mutation<void, number>({
      query: (id) => ({
        url: `/v1/ai/chat/threads/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: SCOUT_THREAD_TAG_TYPE, id: SCOUT_THREAD_LIST_ID },
        { type: SCOUT_THREAD_TAG_TYPE, id },
      ],
    }),
  }),
});

export const {
  useListScoutThreadsQuery,
  useGetScoutThreadQuery,
  useLazyGetScoutThreadQuery,
  useUpdateScoutThreadMutation,
  useDeleteScoutThreadMutation,
} = scoutThreadsApi;
